import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { LoadingBadge } from './LoadingBadge';
import { RiyalIcon } from './icons';
import { useAmountVisibility, maskAmount } from './AmountVisibilityContext';

type Props = {
  links: any[];
  guides: Record<string, any>;
  pilgrims: Record<string, any>;
  isFetchingDetails: boolean;
  hasBonusApplied: boolean;
  baseAmount: number;
  bonusAmount: number;
  notOwnerAmount: number;
};

export const PilgrimTable = ({
  links,
  guides,
  pilgrims,
  isFetchingDetails,
  hasBonusApplied,
  baseAmount,
  bonusAmount,
  notOwnerAmount
}: Props) => {
  const { translations, direction } = useLanguage();
  const { showAmounts } = useAmountVisibility();

  // Calculate effective amount per pilgrim based on number of pilgrims
  // If organizer has >= 500 pilgrims => 2500 SAR, else 2000 SAR
  const pilgrimCount = links.length;
  const amountPerPilgrim = pilgrimCount >= 500 ? 2500 : 2000;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
              {translations?.organizerFinance?.tableHeaders?.pilgrim || 'Pilgrim'}
            </th>
            <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
              {translations?.organizerFinance?.tableHeaders?.passport || 'Passport'}
            </th>
            <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
              {translations?.organizerFinance?.tableHeaders?.guide || 'Guide'}
            </th>
            <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
              {translations?.organizerFinance?.tableHeaders?.paymentType || 'Payment Type'}
            </th>
            <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
              {translations?.organizerFinance?.tableHeaders?.amount || 'Amount'}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {links.map((link) => {
            // Get pilgrim details from the fetched pilgrims data
            const pilgrimDetails = pilgrims[link.pilgrimId] || {};
            const isLoadingDetails = !pilgrimDetails.pilgrimName && isFetchingDetails;
            
            return (
              <tr key={link.id} className="hover:bg-gray-50">
                <td className={`px-6 py-4 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                  {isLoadingDetails ? (
                    <LoadingBadge />
                  ) : (
                    pilgrimDetails.pilgrimName || link.pilgrimName || 'Unknown'
                  )}
                </td>
                <td className={`px-6 py-4 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                  {isLoadingDetails ? (
                    <LoadingBadge />
                  ) : (
                    pilgrimDetails.passportNumber || link.passportNumber || 'Unknown'
                  )}
                </td>
                <td className={`px-6 py-4 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                  {isLoadingDetails ? (
                    <LoadingBadge />
                  ) : (
                    guides[link.guideId]?.name || link.guideName || 'Unknown'
                  )}
                </td>
                <td className={`px-6 py-4 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    link.isOwner === false ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'
                  }`}>
                    {link.isOwner === false ?
                      (translations?.organizerFinance?.guidancePayment || 'Not Owner') :
                      (translations?.organizerFinance?.fullPayment || 'Full Payment')}
                  </span>
                </td>
                <td className={`px-6 py-4 font-medium ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                  {link.isOwner === false ? (
                    <span className="text-yellow-600 flex items-center">
                      {maskAmount(notOwnerAmount, showAmounts)} <RiyalIcon className="ml-1" size={16} />
                    </span>
                  ) : (
                    <span className="flex items-center">
                      {maskAmount(amountPerPilgrim, showAmounts)} <RiyalIcon className="ml-1" size={16} />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};